import { contrastRatio, hexToRgb, readableOn } from './color';
import { isFontId, nearestWeight } from './fonts';
import { DEFAULT_PRESET_ID, PRESETS } from './presets';

export const COLOR_KEYS = [
  'primary',
  'primaryContrast',
  'accent',
  'accentContrast',
  'background',
  'surface',
  'surfaceAlt',
  'text',
  'muted',
  'border',
  'sale',
];

const ENUMS = {
  headingCase: ['normal', 'uppercase'],
  tracking: ['tight', 'normal', 'wide'],
  radius: ['none', 'sm', 'md', 'lg'],
  button: ['pill', 'rounded', 'square'],
  hero: ['split', 'full-bleed', 'editorial'],
  productCard: ['minimal', 'framed'],
};
const HEADING_WEIGHTS = [400, 500, 600, 700];

const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

const toHex = (rgb) => `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** Text color on `bg` that meets 4.5:1, keeping the theme's choice when it already does. */
function ensureReadable(fg, bg) {
  const f = hexToRgb(fg);
  const b = hexToRgb(bg);
  if (f && b && contrastRatio(f, b) >= 4.5) return fg;
  return toHex(readableOn(b));
}

/**
 * Any theme (from the API, the local cache or a preset) -> a complete, safe
 * theme in the contract v2 shape. Missing or invalid tokens are filled from the
 * preset the theme names (or the default preset). Never throws.
 *
 * Adds one derived field, `typography.headingWeightResolved`: the requested
 * heading weight snapped to a weight the heading font really has.
 */
export function resolveTheme(input) {
  const t = obj(input);
  const presetId = typeof t.preset === 'string' && PRESETS[t.preset] ? t.preset : DEFAULT_PRESET_ID;
  const base = PRESETS[presetId];

  const c = obj(t.colors);
  const colors = {};
  COLOR_KEYS.forEach((key) => {
    colors[key] = hexToRgb(c[key]) ? c[key].trim().toUpperCase() : base.colors[key];
  });
  colors.primaryContrast = ensureReadable(colors.primaryContrast, colors.primary);
  colors.accentContrast = ensureReadable(colors.accentContrast, colors.accent);

  const ty = obj(t.typography);
  const heading = isFontId(ty.heading) ? ty.heading : base.typography.heading;
  const body = isFontId(ty.body) ? ty.body : base.typography.body;
  const headingWeight = pick(ty.headingWeight, HEADING_WEIGHTS, base.typography.headingWeight);

  const sh = obj(t.shape);
  const ly = obj(t.layout);
  const an = obj(t.announcement);
  const text = typeof an.text === 'string' ? an.text.trim().slice(0, 140) : '';

  return {
    preset: typeof t.preset === 'string' && t.preset.trim() ? t.preset.trim() : presetId,
    colors,
    typography: {
      heading,
      body,
      headingWeight,
      headingWeightResolved: nearestWeight(heading, headingWeight),
      headingCase: pick(ty.headingCase, ENUMS.headingCase, base.typography.headingCase),
      tracking: pick(ty.tracking, ENUMS.tracking, base.typography.tracking),
    },
    shape: {
      radius: pick(sh.radius, ENUMS.radius, base.shape.radius),
      button: pick(sh.button, ENUMS.button, base.shape.button),
    },
    layout: {
      hero: pick(ly.hero, ENUMS.hero, base.layout.hero),
      productCard: pick(ly.productCard, ENUMS.productCard, base.layout.productCard),
    },
    // An announcement is only shown when it is switched on and has text.
    announcement:
      'enabled' in an
        ? { enabled: an.enabled === true && text.length > 0, text }
        : { ...base.announcement },
  };
}
